import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import dizeeLogo from "../../assets/images/dizee logo.svg";
import OrangeOrb3D from "./OrangeOrb3D";
import { cleanNavigationCommands } from "../Agent/NavigationInterceptor";
import { TourOptions } from "../InteractiveTour/TourOptions";
import { TourPresetQuestions } from "../InteractiveTour/TourPresetQuestions";

const HeroContainer = styled.section`
  min-height: 100vh;
  background: linear-gradient(180deg, #000000 0%, #0a0a0a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(60px, 10vh, 80px) 5% clamp(60px, 10vh, 80px);
  position: relative;
  
  @media (max-width: 1024px) {
    padding: clamp(60px, 8vh, 80px) 4% clamp(60px, 8vh, 80px);
  }
  
  @media (max-width: 768px) {
    padding: 80px 20px 60px;
    min-height: auto;
  }
  
  @media (max-width: 480px) {
    padding: 60px 16px 40px;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(40px, 8vw, 60px);
  text-align: center;
`;

const CenterStage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(40px, 8vw, 60px);
  width: 100%;
`;

const OrbWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: clamp(10px, 3vw, 20px);
  position: relative;
  z-index: 100;
  
  &::before {
    content: '';
    position: absolute;
    width: ${props => props.isTyping ? '120%' : '100%'};
    height: ${props => props.isTyping ? '120%' : '100%'};
    background: ${props => props.isTyping 
      ? 'radial-gradient(circle, rgba(255, 35, 0, 0.35) 0%, rgba(255, 35, 0, 0.22) 25%, rgba(255, 35, 0, 0.12) 40%, rgba(255, 35, 0, 0.06) 55%, transparent 70%)'
      : 'radial-gradient(circle, rgba(255, 35, 0, 0.05) 0%, rgba(255, 35, 0, 0.02) 30%, transparent 60%)'
    };
    filter: ${props => props.isTyping ? 'blur(50px)' : 'blur(20px)'};
    z-index: -1;
    pointer-events: none;
    transition: opacity 6s cubic-bezier(0.2, 0, 0.1, 1), 
                filter 6s cubic-bezier(0.2, 0, 0.1, 1), 
                transform 6s cubic-bezier(0.2, 0, 0.1, 1), 
                width 5s cubic-bezier(0.2, 0, 0.1, 1), 
                height 5s cubic-bezier(0.2, 0, 0.1, 1), 
                background 6s cubic-bezier(0.2, 0, 0.1, 1);
    ${props => props.isTyping && `
      animation: glowPulse 1.2s ease-in-out infinite;
    `}
    
    @media (max-width: 768px) {
      width: ${props => props.isTyping ? '140%' : '100%'};
      height: ${props => props.isTyping ? '140%' : '100%'};
      background: ${props => props.isTyping 
        ? 'radial-gradient(circle, rgba(255, 35, 0, 0.5) 0%, rgba(255, 35, 0, 0.3) 25%, rgba(255, 35, 0, 0.18) 40%, rgba(255, 35, 0, 0.09) 55%, transparent 70%)'
        : 'radial-gradient(circle, rgba(255, 35, 0, 0.05) 0%, rgba(255, 35, 0, 0.02) 30%, transparent 60%)'
      };
      filter: ${props => props.isTyping ? 'blur(60px)' : 'blur(20px)'};
    }
  }
  
  @keyframes glowPulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
      filter: blur(50px);
    }
    25% {
      opacity: 1.2;
      transform: scale(1.05) translateX(-5px);
      filter: blur(55px);
    }
    50% {
      opacity: 1.3;
      transform: scale(1.08) translateY(5px);
      filter: blur(52px);
    }
    75% {
      opacity: 1.15;
      transform: scale(1.03) translateX(5px);
      filter: blur(52px);
    }
    
    @media (max-width: 768px) {
      0%, 100% {
        filter: blur(60px);
      }
      25%, 50%, 75% {
        filter: blur(65px);
      }
    }
  }
`;

const IntroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  padding: 0;
  margin-top: clamp(80px, 10vw, 120px);
  
  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
    margin-top: 140px;
  }
`;

const OrbChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(40px, 8vw, 60px);
  width: 100%;
  align-self: center;
`;

const AppsRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 60px;
  align-items: center;
  width: 100%;
  margin-top: 40px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const AppsTextSection = styled.div`
  text-align: left;
`;

const AppsTitle = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 600;
  color: white;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin: 0 0 16px 0;
`;

const AppsSubtitle = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0;
  max-width: 500px;
  
  @media (max-width: 900px) {
    max-width: 100%;
  }
`;

const IntroHeadline = styled(motion.h1)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  line-height: 1.3;
  letter-spacing: -0.01em;
  margin: 0;
  width: 100%;
  overflow: visible;
  
  @media (max-width: 768px) {
    font-size: clamp(24px, 7vw, 32px);
  }
`;

const IntroDescription = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  margin: 0;
  max-width: 450px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const AskDizeeText = styled(motion.span)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #FF2300;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 90px);
  gap: 28px 32px;
  align-items: start;
  justify-content: center;
  justify-items: center;

  @media (max-width: 900px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 90px);
  }
`;

const AppTile = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const AppIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  background: ${props => props.color || '#2E2E2E'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AppLabel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
`;

const LeftIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  
  @media (max-width: 1024px) {
    align-items: center;
  }
`;

const IntroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Headline = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 600;
  color: white;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin: 0;
  
  @media (max-width: 1024px) {
    text-align: left;
  }
`;

const Subtext = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0;
  
  @media (max-width: 1024px) {
    text-align: center;
    max-width: 600px;
  }
`;

const RightChatArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
`;

const CardTitle = styled.div`
  text-align: center;
  color: rgba(255,255,255,0.8);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 400;
  margin-top: 8px;
`;





// AI Chat (compact)
const AIChatContainer = styled(motion.div)`
  width: 100%;
  max-width: 390px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    padding: 0 10px;
    max-width: 100%;
  }
`;

const AIChatBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 2vw, 16px);
`;

const ConversationArea = styled.div`
  width: 100%;
  height: clamp(140px, 20vh, 180px);
  min-height: clamp(140px, 20vh, 180px);
  max-height: clamp(140px, 20vh, 180px);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 32px;
  padding: 16px 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  
  /* Fade out gradient at top */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
    pointer-events: none;
    z-index: 10;
  }
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const AIMessage = styled(motion.div)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(14px, 2.5vw, 16px);
  font-weight: 400;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  align-self: ${props => props.role === 'user' ? 'flex-end' : 'flex-start'};
  max-width: 75%;
  text-align: ${props => props.role === 'user' ? 'right' : 'left'};
  
  @media (max-width: 480px) {
    max-width: 85%;
  }
`;

const NavigationMessage = styled(motion.div)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(11px, 2vw, 12px);
  font-weight: 400;
  line-height: 1.6;
  color: #8E8E93;
  align-self: flex-start;
  max-width: 85%;
  text-align: left;
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const PromptsContainer = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  width: 100%;
  margin-bottom: 8px;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PromptText = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  color: rgba(255, 255, 255, 0.5);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const AIInputRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
`;

const AIInput = styled.input`
  flex: 1;
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid #FF2300;
  border-radius: 50px;
  padding: 12px 52px 12px 20px;
  color: rgba(255, 255, 255, 0.9);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  outline: none;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(255, 35, 0, 0.3), 0 0 40px rgba(255, 35, 0, 0.15);

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    border-color: #FF2300;
    box-shadow: 0 0 30px rgba(255, 35, 0, 0.4), 0 0 60px rgba(255, 35, 0, 0.2);
  }
`;

const AISend = styled.button`
  position: absolute;
  right: 4px;
  background: ${props => props.enabled ? '#FF2300' : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.enabled ? '#000000' : 'rgba(255, 255, 255, 0.3)'};
  font-size: 18px;
  cursor: ${props => props.enabled ? 'pointer' : 'not-allowed'};
  transition: all 0.3s ease;

  &:hover {
    ${props => props.enabled && `
      background: #ff3a1a;
      transform: scale(1.05);
    `}
  }
`;

const AICategories = styled.div`
  display: flex;
  gap: 24px;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 8px;
  width: 100%;
`;

const AICategory = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ? '#FF2300' : 'rgba(255, 255, 255, 0.45)'};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.active ? '500' : '400'};
  cursor: pointer;
  transition: all 0.25s ease;
  padding: 0;

  &:hover {
    color: ${props => props.active ? '#FF2300' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

const AISuggestions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 700px;
`;

const AISuggestion = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 11px 18px;
  color: rgba(255, 255, 255, 0.8);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 16px;
  }
`;

const CodeEditorContainer = styled(motion.div)`
  width: 100%;
  max-width: 1000px;
  margin: 40px 0;
  position: relative;
`;

const CodeEditor = styled.div`
  background: #1e1e1e;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const EditorHeader = styled.div`
  background: #2d2d2d;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const EditorDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const EditorTitle = styled.span`
  font-family: 'SF Mono', 'Monaco', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-left: auto;
`;

const EditorBody = styled.div`
  padding: 24px;
  font-family: 'SF Mono', 'Monaco', monospace;
  font-size: 14px;
  line-height: 1.6;
  color: #d4d4d4;
  min-height: 300px;
  
  .comment { color: #6a9955; }
  .keyword { color: #569cd6; }
  .string { color: #ce9178; }
  .function { color: #dcdcaa; }
  .variable { color: #9cdcfe; }
  .number { color: #b5cea8; }
`;

const CTAContainer = styled(motion.div)`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  
  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.button`
  background: white;
  color: black;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
    color: white;
  }
`;

const OrbContainer = styled(motion.div)`
  width: clamp(280px, 50vw, 400px);
  height: clamp(280px, 50vw, 400px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    width: clamp(250px, 70vw, 300px);
    height: clamp(250px, 70vw, 300px);
  }
  
  @media (max-width: 480px) {
    width: clamp(220px, 80vw, 280px);
    height: clamp(220px, 80vw, 280px);
  }
`;

const Hero = ({ onGetStarted, onSignIn, onLearnMore }) => {
  const navigate = useNavigate();
  const codeExample = `<span class="comment">// Your music business, automated</span>
<span class="keyword">import</span> { <span class="variable">DIZEE</span> } <span class="keyword">from</span> <span class="string">'@dizee/ai'</span>

<span class="keyword">const</span> <span class="function">musicBusiness</span> = <span class="keyword">new</span> <span class="function">DIZEE</span>({
  <span class="variable">platforms</span>: [<span class="string">'spotify'</span>, <span class="string">'apple-music'</span>, <span class="string">'bandcamp'</span>],
  <span class="variable">analytics</span>: <span class="keyword">true</span>,
  <span class="variable">automation</span>: <span class="keyword">true</span>
})

<span class="comment">// Get comprehensive insights</span>
<span class="keyword">const</span> <span class="variable">insights</span> = <span class="keyword">await</span> musicBusiness.<span class="function">getInsights</span>()
<span class="comment">// → { streams: 2.3M, revenue: $47K, growth: +45% }</span>

<span class="comment">// Automate your workflow</span>
musicBusiness.<span class="function">automate</span>({
  <span class="variable">royaltyCollection</span>: <span class="keyword">true</span>,
  <span class="variable">playlistSubmissions</span>: <span class="keyword">true</span>,
  <span class="variable">fanEngagement</span>: <span class="keyword">true</span>
})

<span class="comment">// Never miss an opportunity</span>
musicBusiness.<span class="function">on</span>(<span class="string">'opportunity'</span>, (opportunity) => {
  console.<span class="function">log</span>(<span class="string">\`New opportunity: \${opportunity.type}\`</span>)
  <span class="comment">// Auto-handle based on your preferences</span>
})`;

  // AI Chat state
  const [aiInput, setAiInput] = useState("");
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCategory, setAiCategory] = useState("General");
  const [typingState, setTypingState] = useState({ isTyping: false, word: "", tick: 0 });
  const [typingBuffer, setTypingBuffer] = useState("");
  const [animationKey, setAnimationKey] = useState(0);
  const [noAnimateIndex, setNoAnimateIndex] = useState(-1);
  const conversationRef = useRef(null);

  // Clear chat on mount
  useEffect(() => {
    localStorage.removeItem('homepage_chat');
    setAiMessages([]);
  }, []);

  // Auto-scroll to bottom when messages change (only if there are messages)
  useEffect(() => {
    if (conversationRef.current && (aiMessages.length > 0 || typingBuffer)) {
      const scrollContainer = conversationRef.current;
      // Smooth scroll to bottom
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [aiMessages.length, typingBuffer]);


  // Remove the localStorage persistence
  // useEffect(() => {
  //   try {
  //     const saved = localStorage.getItem('homepage_chat');
  //     if (saved) {
  //       const parsed = JSON.parse(saved);
  //       if (Array.isArray(parsed)) {
  //         setAiMessages(parsed);
  //       }
  //     }
  //   } catch {}
  // }, []);

  // useEffect(() => {
  //   try {
  //     localStorage.setItem('homepage_chat', JSON.stringify(aiMessages));
  //   } catch {}
  // }, [aiMessages]);

  const aiSuggestions = {
    General: [
      "Career Health Check",
      "Year in Review"
    ],
    Music: [
      "Connect Calendar",
      "Manage Team",
      "Teach Me Something New"
    ],
    Shows: [
      "How Can I Better Utilize Dizee",
      "Restart Tutorial"
    ],
    Revenue: []
  };

  const aiResponses = {
    // General prompts
    "What do I have to do today?": "Got it. I'll analyze your catalogue, recent trends, and audience to recommend the most impactful actions.",
    "What do I have to do tomorrow?": "I'll check your upcoming releases, scheduled posts, and show commitments to prioritize tomorrow's tasks.",
    "What do I have to do this week?": "Here's your week ahead: 2 playlist submissions due, 1 social campaign to review, and 3 audience engagement opportunities.",
    
    // Music prompts
    "How many songs released?": "You've released 47 tracks total. 12 were released this year, with your last single dropping 3 weeks ago.",
    "What should I release next month?": "Based on your streaming trends and audience engagement, I recommend releasing your unreleased track 'Midnight Drive' - it aligns with your top-performing genre.",
    "Where am I growing fastest?": "Your fastest growth is in Austin, TX (+127% last month) and Berlin, Germany (+94%). Consider targeting these markets.",
    
    // Shows prompts
    "How many shows played?": "You've played 23 shows this year across 8 cities. Average attendance: 340 people per show.",
    "How many sold out shows?": "You've had 4 sold-out shows this year: 2 in Brooklyn, 1 in Austin, and 1 in Portland. Great momentum!",
    "How many tickets sold?": "You've sold 7,820 tickets total this year, with an average sell-through rate of 78% across all venues.",
    
    // Revenue prompts
    "How many merch items sold?": "You've sold 1,247 merch items this year. Top sellers: tour t-shirts (523), vinyl (189), and hoodies (156).",
    "Why did revenue dip last week?": "Revenue dipped 23% last week due to lower streaming numbers between releases. This is normal - your next single drops in 5 days which should reverse the trend.",
    "Which release earns the most?": "Your track 'Echoes' is your top earner at $12,400 total revenue, followed by 'City Lights' at $9,800. Both benefit from playlist placements.",
    
    // Default for custom input
    default: "Tap or click one of the preset prompts to see how DIZEE can help manage your music business."
  };

  const handleSend = (text) => {
    const source = aiInput.trim();
    const prompt = (text || source).trim();
    if (!prompt || aiLoading) return;

    // Check if this is the "Restart Tutorial" prompt
    if (prompt === "Restart Tutorial") {
      // Clear all walkthrough and tutorial flags
      localStorage.removeItem('dizeeWalkthroughCompleted');
      localStorage.removeItem('hasSeenWalkthrough');
      localStorage.removeItem('hasSeenTutorial');
      localStorage.removeItem('onboardingCompletedDate');
      localStorage.removeItem('dailyDigestLastShown');
      localStorage.removeItem('accountSetupCompleted');
      
      // Clear checklist state
      localStorage.removeItem('setupChecklistCompleted');
      localStorage.removeItem('setupChecklistItems');
      
      // Clear tour state
      localStorage.removeItem('tourCompleted');
      sessionStorage.removeItem('tourActive');
      sessionStorage.removeItem('tourCurrentSection');
      sessionStorage.removeItem('tourStartTime');
      sessionStorage.removeItem('tourMessages');
      
      // Set flag to trigger walkthrough on next landing page visit
      localStorage.setItem('isNewSignup', 'true');
      
      // Check if we're already on the landing page
      if (window.location.pathname === '/') {
        // Force a page reload to restart the tutorial
        window.location.reload();
      } else {
        // Navigate to landing page to start walkthrough
        navigate('/');
      }
      return;
    }

    console.log('🚀 [SEND] User sent message:', prompt);
    setAiMessages(prev => {
      const updated = [...prev, { role: 'user', text: prompt }];
      console.log('📝 [STATE] aiMessages updated:', updated.length, 'messages');
      return updated;
    });
    setAiInput("");
    setAiLoading(true);
    console.log('⏳ [STATE] aiLoading: true');

    // Get specific response for the prompt
    const reply = (aiResponses[prompt] || aiResponses.default).trim();

    // Typewriter effect (slower and smoother)
    setTypingBuffer("");
    console.log('🔄 [STATE] typingBuffer cleared');
    setTypingState({ isTyping: true, word: "", tick: 0 });
    console.log('⌨️  [STATE] typingState: { isTyping: true }');

    const typeNext = (i, buf) => {
      if (i >= reply.length) {
        console.log('✅ [TYPING] Typing complete, reply length:', reply.length);
        // Add a natural pause at the very end before any state changes
        setTimeout(() => {
          // Stop typing state (stops glow, removes orange highlight)
          // Keep typing buffer visible during glow fade
          console.log('🛑 [STATE] Stopping typing state (glow starts fading)');
          setTypingState({ isTyping: false, word: "", tick: 0 });
          console.log('⌨️  [STATE] typingState: { isTyping: false }');
          
          // After glow fades completely, add message to array and clear buffer
          // Use a longer delay to let the glow fully fade out smoothly (6s glow fade)
          setTimeout(() => {
            console.log('🔄 [TRANSITION] Starting buffer-to-array transition');
            
            // Add message to array and get the correct index
            setAiMessages(prev => {
              const updated = [...prev, { role: 'assistant', text: buf }];
              const newMessageIndex = updated.length - 1; // Index of the message we just added
              console.log('📝 [STATE] aiMessages updated:', updated.length, 'messages (assistant reply added at index', newMessageIndex, ')');
              
              // Set noAnimateIndex AFTER we know the correct index
              setNoAnimateIndex(newMessageIndex);
              console.log('🎯 [STATE] noAnimateIndex set to:', newMessageIndex);
              
              return updated;
            });
            
            // Clear buffer at exact same time
            setTypingBuffer("");
            console.log('🔄 [STATE] typingBuffer cleared (transition complete)');
            setAiLoading(false);
            console.log('⏳ [STATE] aiLoading: false');
            console.log('✨ [COMPLETE] Message fully transitioned to array');
          }, 7000); // Increased to 7000 to match 6s transition + 1s buffer
        }, 1500);
        return;
      }
      const nextBuf = buf + reply[i];
      setTypingBuffer(nextBuf);
      const match = nextBuf.match(/(\S+)$/);
      const lastWord = match ? match[1] : "";
      setTypingState(prev => ({ isTyping: true, word: lastWord, tick: prev.tick + (reply[i] === ' ' ? 5 : 1) }));
      const ch = reply[i];
      const isPause = /[\.,!?\n]/.test(ch) || ch === ' ';
      
      // Slow down near the end for ultra-smooth finish
      const remainingChars = reply.length - i;
      const isNearEnd = remainingChars <= 30;
      const endSlowdown = isNearEnd ? (1 + (30 - remainingChars) * 0.12) : 1;
      
      const baseDelay = (isPause ? 60 : 28);
      const randomVariation = Math.floor(Math.random() * (isPause ? 30 : 12));
      const delay = (baseDelay + randomVariation) * endSlowdown;
      
      setTimeout(() => typeNext(i + 1, nextBuf), delay);
    };

    typeNext(0, "");
  };

  return (
    <HeroContainer>
      <HeroContent>
        <CenterStage>
          <OrbChatWrapper>
            <OrbWrapper
              isTyping={typingState.isTyping}
            >
              <OrbContainer
                key={animationKey}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <OrangeOrb3D />
              </OrbContainer>
            </OrbWrapper>
            
            <AIChatContainer
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
            <AIChatBox>
              {/* Conversation Area */}
              <ConversationArea ref={conversationRef}>
                {/* Show last 3 messages only if there are messages */}
                {/* Don't show the last assistant message if typing buffer is showing it */}
                {aiMessages.slice(-3).map((m, i) => {
                  const absoluteIndex = aiMessages.length - 3 + i;
                  const shouldAnimate = absoluteIndex !== noAnimateIndex;
                  const isLastMessage = absoluteIndex === aiMessages.length - 1;
                  
                  // Skip rendering the last assistant message if typing buffer exists with content
                  if (isLastMessage && typingBuffer && typingBuffer.length > 0 && m.role === 'assistant') {
                    console.log('🚫 [RENDER] Skipping last assistant message (buffer active)');
                    return null;
                  }
                  
                  console.log(`📊 [RENDER] Message ${absoluteIndex}: ${m.role}, shouldAnimate: ${shouldAnimate}, isLast: ${isLastMessage}`);
                  
                  // Use NavigationMessage component for navigation messages
                  const MessageComponent = m.isNavigationMessage ? NavigationMessage : AIMessage;
                  
                  return (
                    <MessageComponent 
                      key={absoluteIndex} 
                      role={m.role}
                      initial={shouldAnimate ? { opacity: 0, y: 10 } : { opacity: 0, y: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: shouldAnimate ? 0.4 : 0.6,
                        ease: shouldAnimate ? "easeOut" : [0.4, 0, 0.2, 1]
                      }}
                    >
                      {cleanNavigationCommands(m.text)}
                    </MessageComponent>
                  );
                })}
                
                {/* Typing indicator - only show if buffer has content */}
                {(() => {
                  const shouldShowBuffer = typingBuffer && typingBuffer.length > 0;
                  if (shouldShowBuffer) {
                    console.log(`💬 [RENDER] Showing typing buffer (${typingBuffer.length} chars), isTyping: ${typingState.isTyping}`);
                  }
                  return shouldShowBuffer;
                })() && (
                  <AIMessage 
                    role="assistant"
                    initial={{ opacity: 1, y: 0 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0 
                    }}
                    exit={{ 
                      opacity: 0
                    }}
                    transition={{ 
                      duration: 0,
                      exit: { duration: 0.8, ease: [0.4, 0, 0.2, 1] }
                    }}
                  >
                    {(() => {
                      const buf = cleanNavigationCommands(typingBuffer);
                      const match = buf.match(/(\S+)$/);
                      const last = match ? match[1] : "";
                      const head = last ? buf.slice(0, -last.length) : buf;
                      return (
                        <>
                          <span>{head}</span>
                          {/* Only highlight last word in orange when actively typing */}
                          <span style={{ color: typingState.isTyping ? '#FF2300' : 'rgba(255, 255, 255, 0.85)' }}>{last}</span>
                        </>
                      );
                    })()}
                  </AIMessage>
                )}
              </ConversationArea>

              {/* Prompt suggestions - horizontal scroll */}
              <PromptsContainer>
                {aiSuggestions[aiCategory].map((s, idx) => (
                  <PromptText key={idx} onClick={() => { setAiInput(s); }}>
                    {s}
                  </PromptText>
                ))}
              </PromptsContainer>

              {/* Input row */}
              <AIInputRow>
                <AIInput
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && aiInput.trim() && handleSend()}
                  placeholder="Ask me anything..."
                  aria-label="Ask DIZEE"
                />
                <AISend onClick={() => handleSend()} enabled={aiInput.trim().length > 0}>
                  ↗
                </AISend>
              </AIInputRow>

              {/* Categories */}
              <AICategories>
                {Object.keys(aiSuggestions).map(cat => (
                  <AICategory 
                    key={cat} 
                    active={aiCategory === cat} 
                    onClick={() => { 
                      setAiCategory(cat); 
                      setAiInput(""); 
                    }}
                  >
                    {cat}
                  </AICategory>
                ))}
              </AICategories>
            </AIChatBox>
          </AIChatContainer>
          </OrbChatWrapper>

          <IntroSection>
            <IntroHeadline>
              {['Your', 'music', 'business', 'on', 'autopilot'].map((word, i) => (
                <motion.span
                  key={i}
                  style={{ display: 'inline-block', marginRight: word === 'autopilot' ? '0' : '0.25em' }}
                  initial={{ 
                    opacity: 0, 
                    scale: 0.3,
                    rotateX: -90,
                    filter: 'blur(10px)'
                  }}
                  whileInView={{ 
                    opacity: 1, 
                    scale: 1,
                    rotateX: 0,
                    filter: 'blur(0px)'
                  }}
                  viewport={{ once: true, margin: "0px" }}
                  transition={{ 
                    duration: 0.8, 
                    delay: i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                    scale: { type: "spring", stiffness: 200, damping: 20 }
                  }}
                >
                  {word === 'autopilot' ? (
                    <motion.span
                      style={{ 
                        background: 'linear-gradient(90deg, #FF2300, #FF6B35, #FF2300)',
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        display: 'inline-block'
                      }}
                      animate={{
                        backgroundPosition: ['0% 0%', '200% 0%']
                      }}
                      transition={{
                        duration: 3,
                        ease: "linear",
                        repeat: Infinity
                      }}
                    >
                      {word}
                    </motion.span>
                  ) : word}
                </motion.span>
              ))}
            </IntroHeadline>
            <IntroDescription
              initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              The AI-powered workspace that knows your catalogue, touring, marketing, revenue and audience and turns it into actions
            </IntroDescription>
          </IntroSection>
        </CenterStage>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero; 